import { useState } from "react";
import { Plus } from "lucide-react";
import { useTranslations } from "use-intl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { countryFromVatNumber, countryNameFor } from "@/services/invoiceGeneration";
import type { Customer, CustomerType, NewCustomerInput } from "@/services/invoiceGeneration";
import { userFacingErrorMessage } from '@/utils/userFacingError';

interface Props {
  customers: Customer[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onCreate: (input: NewCustomerInput) => Promise<void>;
}

const EMPTY = {
  name: "", type: "company" as CustomerType, orgNumber: "", vatNumber: "",
  country: "SE", countryName: "Sweden", email: "",
  street: "", postalCode: "", city: "",
};

export function CustomerPicker({ customers, selectedId, onSelect, onCreate }: Props) {
  const t = useTranslations("consultantReceivables.customerPicker");
  const tActions = useTranslations("common.actions");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY);

  const set = (patch: Partial<typeof EMPTY>) => setForm((f) => ({ ...f, ...patch }));

  async function submit() {
    if (!form.name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await onCreate({
        name: form.name.trim(),
        type: form.type,
        orgNumber: form.orgNumber || undefined,
        vatNumber: form.vatNumber || undefined,
        country: form.country,
        countryName: form.countryName,
        email: form.email || undefined,
        address: {
          street: form.street, postalCode: form.postalCode, city: form.city, country: form.countryName,
        },
      });
      setForm(EMPTY);
      setOpen(false);
    } catch (err) {
      // Was previously uncaught — the dialog would silently stay open with no
      // feedback at all if createCustomer failed. Never leave a failure invisible.
      setError(userFacingErrorMessage(err, t("genericError")));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex items-end gap-2">
      <div className="flex-1">
        <Label className="mb-1.5 block">{t("label")} <span className="text-destructive">*</span></Label>
        <Select value={selectedId ?? undefined} onValueChange={onSelect}>
          <SelectTrigger>
            <SelectValue placeholder={t("placeholder")} />
          </SelectTrigger>
          <SelectContent>
            {customers.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
                {customerSuffix(c, t("private"))}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Dialog open={open} onOpenChange={(next) => { setOpen(next); if (!next) setError(null); }}>
        <DialogTrigger asChild>
          <Button type="button" variant="ghost" className="text-primary">
            <Plus size={16} className="mr-1" /> {t("newCustomer")}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("newCustomer")}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            <span className="text-destructive">*</span> {t("requiredNote")}
          </p>
          <div className="space-y-3">
            <div>
              <Label className="mb-1.5 block">{t("type")}</Label>
              <Select value={form.type} onValueChange={(v) => set({ type: v as CustomerType })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">{t("company")}</SelectItem>
                  <SelectItem value="individual">{t("individual")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Field label={t("name")} required value={form.name} onChange={(v) => set({ name: v })} />
            <div className="grid grid-cols-2 gap-3">
              {form.type === "company" && (
                <Field label={t("orgNumber")} value={form.orgNumber} onChange={(v) => set({ orgNumber: v })} />
              )}
              <Field
                label={t("vatNumber")} value={form.vatNumber}
                onChange={(v) => {
                  // A VAT number starts with its issuing country's prefix (SE, DE, ...) —
                  // once one is entered, derive the country from it instead of asking twice.
                  const derived = countryFromVatNumber(v);
                  set(derived ? { vatNumber: v, country: derived, countryName: countryNameFor(derived) } : { vatNumber: v });
                }}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Field label={t("country")} value={form.country}
                  onChange={(v) => set({ country: v.toUpperCase(), countryName: countryNameFor(v) })} />
                {countryFromVatNumber(form.vatNumber) && (
                  <p className="mt-1 text-xs text-muted-foreground">{t("fromVat")}</p>
                )}
              </div>
              <Field label={t("email")} value={form.email} onChange={(v) => set({ email: v })} />
            </div>
            <Field label={t("street")} value={form.street} onChange={(v) => set({ street: v })} />
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("postalCode")} value={form.postalCode} onChange={(v) => set({ postalCode: v })} />
              <Field label={t("city")} value={form.city} onChange={(v) => set({ city: v })} />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>{tActions("cancel")}</Button>
            <Button onClick={submit} disabled={!form.name.trim() || saving}>
              {saving ? t("saving") : t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** What follows a customer's name in the picker: its country when foreign, "Private" for a Swedish individual. */
function customerSuffix(customer: Customer, privateLabel: string): string {
  if (customer.country !== "SE") return ` · ${customer.countryName}`;
  return customer.type === "individual" ? ` · ${privateLabel}` : "";
}

function Field({ label, value, onChange, required }: { label: string; value: string; onChange: (v: string) => void; required?: boolean }) {
  return (
    <div>
      <Label className="mb-1.5 block">{label} {required && <span className="text-destructive">*</span>}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
